import React, { useState } from "react";
import Image from "react-bootstrap/Image";
import { Button } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { IMAGES } from "../../../assets/images/images";
import { Icon } from "@iconify/react";
import { Formik, Field, ErrorMessage, Form } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import { verifyForgotPassword } from "../../../utility/apiServices/userServices";

const UserResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location?.state?.email;
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const ResetPasswordSchema = Yup.object().shape({
    otp: Yup.string().required(t("resetpassword.otp_required") || "OTP is required"),
    newpassword: Yup.string()
      .min(8, t("resetpassword.newpassword_min") || "Password must be at least 8 characters")
      .required(t("resetpassword.newpassword_required") || "New Password is required"),
    cnewpassword: Yup.string()
      .oneOf([Yup.ref("newpassword"), null], t("resetpassword.passwords_match") || "Passwords must match")
      .required(t("resetpassword.cnewpassword_required") || "Confirm New Password is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await verifyForgotPassword({
        email: email,
        otp: values.otp,
        newPassword: values.newpassword,
      });
      if (response?.status) {
        Swal.fire({
          icon: "success",
          text: response.message || t("resetpassword.success_message") || "Password reset successful",
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/login");
          }
        });
      } else {
        Swal.fire({
          icon: "error",
          text: response.message || t("resetpassword.error_message") || "Failed to reset password",
        });
      }
    } catch (error) {
      console.error("Reset password error", error);
      Swal.fire({
        icon: "error",
        text: t("resetpassword.general_error") || "An error occurred",
      });
    }
    setSubmitting(false);
  };

  return (
    <section className="login_box">
      <div className="container-fluid">
        <div className="row align-items-center">
          <div className="col-md-6 hideloginimg">
            <div className="login_bg">
              <Image src={IMAGES.Login_Bg} className="img-fluid" />
            </div>
          </div>
          <div className="col-md-6">
            <div className="loginBody">
              <Formik
                initialValues={{ otp: "", newpassword: "", cnewpassword: "" }}
                validationSchema={ResetPasswordSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, values, setFieldValue }) => (
                  <Form>
                    <h1>{t("resetpassword.title") || "Reset Password"}</h1>
                    <p className="para">
                      {t("resetpassword.description") || "Please fill the details below to reset your password"}
                    </p>

                    <div className="mb-3">
                      <label htmlFor="otp" className="mb-1">
                        {t("resetpassword.otp_label") || "Enter OTP"}
                      </label>
                      <div className="otpBox">
                        <OtpInput
                          value={values.otp}
                          onChange={(e) => setFieldValue("otp", e)}
                          numInputs={6}
                          renderInput={(props) => (
                            <input {...props} type="tel" inputMode="numeric" pattern="[0-9]*" />
                          )}
                        />
                      </div>
                      <ErrorMessage name="otp" component="div" className="errorStyle" />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="newpassword">
                        {t("resetpassword.newpassword_label") || "New Password"}
                      </label>
                      <div className="position-relative">
                        <Field
                          name="newpassword"
                          type={showNewPassword ? "text" : "password"}
                          placeholder={t("resetpassword.newpassword_placeholder") || "Enter new password"}
                          className="form-control"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="password-toggle-btn"
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            padding: "5px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Icon
                            icon={showNewPassword ? "mdi:eye-off" : "mdi:eye"}
                            fontSize={20}
                            color="#666"
                          />
                        </button>
                      </div>
                      <ErrorMessage name="newpassword" component="div" className="errorStyle" />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="cnewpassword">
                        {t("resetpassword.cnewpassword_label") || "Confirm New Password"}
                      </label>
                      <div className="position-relative">
                        <Field
                          name="cnewpassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder={t("resetpassword.cnewpassword_placeholder") || "Enter confirm new password"}
                          className="form-control"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="password-toggle-btn"
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            padding: "5px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Icon
                            icon={showConfirmPassword ? "mdi:eye-off" : "mdi:eye"}
                            fontSize={20}
                            color="#666"
                          />
                        </button>
                      </div>
                      <ErrorMessage name="cnewpassword" component="div" className="errorStyle" />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-primary my-3 mb-4 my-mb-3 mb-md-3 my-lg-3 w-100"
                    >
                      {isSubmitting ? t("resetpassword.submitting") || "Submitting..." : t("resetpassword.continue") || "Continue"}
                    </Button>

                    <p className="text-center">
                      {t("resetpassword.back_to") || "Back To"}{" "}
                      <Link to="/forgotpassword" className="forgotPassword forgotPassword1">
                        {t("resetpassword.forgot_password") || "Forgot Password"}
                      </Link>
                    </p>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UserResetPassword;
